import cryptoJS from "crypto-js";
import { type useRouter } from "next/navigation";
import { toast } from "sonner";

// Helper functions with TypeScript types

/**
 * Returns an ISO String representing the current time plus input in hours.
 *
 * @param {number} hours - The number of hours to offset from the current time.
 * @returns {string} Returns the ISO string format after offset
 */
export function offsetDateTimeByHours(hours: number = 0): string {
  const now = new Date();
  now.setHours(now.getHours() + hours);
  return now.toISOString();
}

export function getLanguageCode(
  languageName: string,
  languageOptions: { name: string; code: string }[],
): string | null {
  for (let i = 0; i < languageOptions.length; i++) {
    if (languageOptions[i].name.toLowerCase() === languageName.toLowerCase()) {
      return languageOptions[i].code;
    }
  }
  return null; // Return null if the language is not found
}

export function getCognitoHash(
  email: string,
  clientId: string,
  clientSecret: string,
): string {
  const hash = cryptoJS.HmacSHA256(email + clientId, clientSecret);
  console.log("Hash: ", cryptoJS.enc.Base64.stringify(hash));
  return cryptoJS.enc.Base64.stringify(hash);
}

export function appendNames(nameFirst: string, surname: string): string {
  return nameFirst + " " + surname;
}

export function convertDateToISO(dateString: string): string {
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
}

export function parseDateForCognito(dateInput: number | string): string {
  let date: Date;
  // Check if input is a number (timestamp)
  if (typeof dateInput === "number") {
    date = new Date(dateInput);
  } else {
    date = new Date(dateInput);
  }

  // If date is invalid, throw an error
  if (isNaN(date.getTime())) {
    throw new Error("Invalid date format");
  }

  // Format the date to 'YYYY-MM-DD'
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function generateTimestamp(): string {
  const timestamp = Math.floor(Date.now() / 1000);
  return timestamp.toString();
}

export function parseImage(
  url: string,
  blurhash: string,
  width: number,
  height: number,
): { url: string; blurhash: string; width: number; height: number } {
  return { url, blurhash, width, height };
}

export function parseDateFromISO(
  dateISO: string,
  dateOnly: boolean = false,
): string {
  const date = new Date(dateISO);
  const optionsDate: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const optionsTime: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  const formattedDate = date.toLocaleDateString("en-US", optionsDate);
  const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

  if (dateOnly) {
    return `${formattedDate}`;
  }
  return `${formattedDate} | ${formattedTime}`;
}

export function parseTimeFromISO(dateISO: string): string {
  const date = new Date(dateISO);
  const optionsTime: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  const formattedTime = date.toLocaleTimeString("en-US", optionsTime);
  return `${formattedTime}`;
}

export function decodeBase64AndSplit(base64String: string): {
  auth: string;
  timestamp: string;
} {
  const decodedString = atob(base64String);
  const parts = decodedString.split(":");
  if (parts.length !== 2) {
    throw new Error(
      "The decoded string does not contain exactly two parts separated by a colon.",
    );
  }
  return { auth: parts[0], timestamp: parts[1] };
}

/**
 * Converts a number into a percentage string.
 *
 * - When `override` is **false** (default):
 *   - Values greater than 1 are capped at 100 %.
 *   - Values less than -1 are capped at -100 %.
 * - When `override` is **true**:
 *   - Values are not clamped; any number is converted directly (e.g., 1.5 → 150 %, –0.4 → –40 %, 2.7 → 270 %).
 * - Result is formatted to a maximum of 2 decimal places.
 *
 * @param value     The number to convert.
 * @param override  If `true`, bypasses clamping and allows values outside –1 … 1. (default: `false`)
 * @returns         A formatted percentage string (e.g., "54.21%").
 */
export function formatPercentage(value: number, override = false): string {
  const processed = override ? value : Math.max(-1, Math.min(1, value));
  const percentage = +(processed * 100).toFixed(2);
  return `${percentage}%`;
}

export function formatAmountCurrency(
  amount: string | number | null | undefined,
  currency: string = "PHP",
): string {
  let finalAmount: number | string = amount ? amount : 0;
  if (amount == null) {
    finalAmount = 0;
  }

  if (typeof amount === "string") {
    amount = parseFloat(amount);
  }

  if (!amount) {
    return `${currency} 0.00`;
  } else {
    const formattedAmount = finalAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currency} ${formattedAmount}`;
  }
}

export function formatPhoneNumber(input: string): string {
  const cleaned = input.replace(/\D/g, "");
  if (cleaned.startsWith("63")) {
    return `+${cleaned}`;
  }
  if (cleaned.startsWith("0")) {
    return `+63${cleaned.substring(1)}`;
  }
  return `+63${cleaned}`;
}

export function formatSmartNumber(input: string | number): string {
  const number =
    typeof input === "string" ? parseFloat(input.replace(/,/g, "")) : input;

  if (isNaN(number)) return "Invalid Number";

  if (number >= 1e9) {
    return (number / 1e9).toFixed(1) + "B";
  } else if (number >= 1e6) {
    return (number / 1e6).toFixed(1) + "M";
  } else if (number >= 1e3) {
    return (number / 1e3).toFixed(1) + "K";
  } else {
    return number.toFixed(0);
  }
}

export function parseSocials(
  platform: "facebook" | "tiktok" | "threads" | "x" | "instagram",
  input: string,
): string | null {
  if (!input) return null;

  const value = input.trim().toLowerCase();

  switch (platform) {
    case "facebook": {
      const facebookRegex =
        /^(?:https?:\/\/)?(?:www\.)?(?:facebook\.com\/|fb\.com\/|fb\.me\/|facebook\.co\/)([a-zA-Z0-9.]+)(?:\/|$)/;
      const match = value.match(facebookRegex);
      return match ? match[1] : value;
    }

    case "tiktok": {
      const tiktokRegex =
        /^(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@([a-zA-Z0-9_.]+)(?:\/|$)/;
      const match = value.match(tiktokRegex);
      return match ? match[1] : value;
    }

    case "threads": {
      const threadsRegex =
        /^(?:https?:\/\/)?(?:www\.)?threads\.net\/@([a-zA-Z0-9_.]+)(?:\/|$)|^@([a-zA-Z0-9_.]+)$/;
      const match = value.match(threadsRegex);
      return match ? match[1] || match[2] : value;
    }

    case "x": {
      const xRegex =
        /^(?:https?:\/\/)?(?:www\.)?x\.com\/@([a-zA-Z0-9_]{1,15})(?:\/|$)|^@([a-zA-Z0-9_]{1,15})$/;
      const match = value.match(xRegex);
      return match ? match[1] || match[2] : value;
    }

    case "instagram": {
      const instagramRegex =
        /^(?:https?:\/\/)?(?:www\.)?(?:instagram\.com\/|ig\.com\/|insta\.com\/)([a-zA-Z0-9_.]{1,30})(?:\/|$)/;
      const match = value.match(instagramRegex);
      return match ? match[1] : value;
    }

    default:
      return value;
  }
}

export function generateRQKey(auth: string): string {
  const now = Math.floor(Date.now() / 1000);
  const appendedKey = now + ":" + auth + ":" + secureReverse(auth, true);
  return btoa(appendedKey);
}

export function getAuthFromRQKey(rqkey: string): string {
  const decodedKey = getDecodedRQKey(rqkey);
  const auth = decodedKey.split(":")[1];
  return auth;
}

export function validateRQKey(rqkey: string): boolean {
  const auth = getAuthFromRQKey(rqkey);
  const secureKey = getSecureKeyFromRQKey(rqkey);
  if (auth !== secureKey) {
    console.error("Access denied. This key is not valid.");
    return false;
  }
  return true;
}

export function getDecodedRQKey(rqkey: string): string {
  return atob(rqkey);
}

export function getSecureKeyFromRQKey(rqkey: string): string | number | object {
  const decodedKey = getDecodedRQKey(rqkey);
  const secureKey = decodeReverse(decodedKey.split(":")[2], "string", true);
  return secureKey;
}

/**
 * Converts input to a string and reverses it.
 * If the input is a number, it is converted to a string.
 * If the input is an object (JSON), it is stringified.
 * If secure is true, it returns a Base64 encoded result.
 * @param {any} input - The input to reverse.
 * @param {boolean} secure - Whether to Base64 encode the result.
 * @returns {string} - The reversed string, possibly encoded.
 */
export function secureReverse(
  input: string | object | number,
  secure: boolean = true,
): string {
  let str: string;
  if (typeof input === "number") {
    str = input.toString();
  } else if (typeof input === "object") {
    str = JSON.stringify(input);
  } else {
    str = input;
  }

  let reversedString = str.split("").reverse().join("");

  if (secure) {
    // reversedString = btoa(reversedString);
    reversedString = Buffer.from(reversedString).toString("base64");
  }

  return reversedString;
}

/**
 * Decodes the reversed string based on the mode provided.
 * If secure is true, the reversed string is decoded from Base64 first.
 * @param {string} reversedString - The reversed string (possibly Base64 encoded).
 * @param {string|null} mode - The mode to decode ('json', 'number', or 'string').
 * @param {boolean} secure - Whether the input is Base64 encoded.
 * @returns {any} - The decoded result based on the mode.
 */
export function decodeReverse(
  reversedString: string,
  mode: string = "string",
  secure: boolean = true,
): string | object | number {
  // If secure is true, decode the reversed string from Base64
  if (secure) {
    reversedString = atob(reversedString);
  }

  const restoredString = reversedString.split("").reverse().join("");

  if (mode === "json") {
    try {
      return JSON.parse(restoredString);
    } catch (error) {
      throw new Error(`Invalid JSON format. ${error}`);
    }
  } else if (mode === "number") {
    const number = parseFloat(restoredString);
    if (!number) {
      throw new Error("Reversed string is not a valid number");
    }
    return number;
  } else {
    return restoredString; // Treat as string if mode is 'string' or null
  }
}

// utils/debounce.ts
export const debounce = <Args extends unknown[]>(
  func: (...args: Args) => void,
  delay: number,
) => {
  let timeoutId: NodeJS.Timeout | undefined;

  const debouncedFunction = (...args: Args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };

  debouncedFunction.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  return debouncedFunction;
};

// utils/throttle.ts
export const throttle = <Args extends unknown[]>(
  func: (...args: Args) => void,
  limit: number,
) => {
  let inThrottle: boolean;
  let lastArgs: Args | null = null;

  return (...args: Args) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          func(...lastArgs);
          lastArgs = null;
        }
      }, limit);
    } else {
      lastArgs = args;
    }
  };
};

// Encryption function
export function encryptPayload(
  base64String: string,
  dtime: number,
  bytes = false,
): number[] | string {
  // Validate dtime value
  if (dtime < 0 || dtime > 255 || !Number.isInteger(dtime)) {
    throw new Error("dtime must be an integer between 0 and 255.");
  }

  // Convert the base64 string to a byte array
  const byteArray = Uint8Array.from(atob(base64String), (char) =>
    char.charCodeAt(0),
  );

  // Create a new array to store the encrypted data
  const encryptedArray: number[] = [];

  // Insert the dtime value between every byte
  for (let i = 0; i < byteArray.length; i++) {
    encryptedArray.push(byteArray[i]);
    encryptedArray.push(dtime); // Insert the dtime byte after each byte
  }

  // If 'bytes' flag is true, return the byte array
  if (bytes) {
    return encryptedArray;
  }

  // Convert the array to base64 string
  return btoa(String.fromCharCode(...encryptedArray));
}

// Decryption function
export function decryptPayload(
  base64String: string,
  bytes = false,
): number[] | string {
  // Convert base64 to byte array
  const byteArray = Uint8Array.from(atob(base64String), (char) =>
    char.charCodeAt(0),
  );

  // Validate if the byte array length is even (to ensure correct pattern)
  if (byteArray.length % 2 !== 0) {
    throw new Error("Invalid byte array length, cannot be decrypted.");
  }

  // Extract dtime from the first occurrence
  const dtime = byteArray[1];

  // Validate dtime pattern
  for (let i = 1; i < byteArray.length; i += 2) {
    if (byteArray[i] !== dtime) {
      throw new Error("Inconsistent dtime found in the byte array.");
    }
  }

  // Reconstruct the original byte array
  const originalArray: number[] = [];
  for (let i = 0; i < byteArray.length; i += 2) {
    originalArray.push(byteArray[i]);
  }

  // Return original byte array or base64 encoded string based on 'bytes' flag
  if (bytes) {
    return originalArray;
  }

  // Convert back to base64 string
  return btoa(String.fromCharCode(...originalArray));
}

// Prepare Blob file from various input types
export function prepareToBlobFile(data: unknown): Blob {
  let blob: Blob;

  if (typeof data === "object" && data !== null) {
    // JSON object to Blob
    const jsonString = JSON.stringify(data);
    blob = new Blob([jsonString], { type: "application/json" });
  } else if (typeof data === "string") {
    // Base64 string to Blob
    const byteCharacters = atob(data);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    blob = new Blob([byteNumbers], { type: "application/octet-stream" });
  } else if (data instanceof Uint8Array || Array.isArray(data)) {
    // Byte array to Blob
    blob = new Blob([...data], { type: "application/octet-stream" });
  } else {
    throw new Error(
      "Unsupported data type. Provide an object, Base64 string, or byte array.",
    );
  }

  return blob;
}

// Enhanced version with proper TypeScript types:
export const prepareDownloadAnchor = (fileType: string, filename: string, fullFileName?: string) => {
  const fullFilename = !!fullFileName?.trim() ? fullFileName : `${filename}.${fileType}`;

  // Check if running as PWA
  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any).standalone === true;

  return {
    trigger: async (blob: Blob) => {
      // ✅ CRITICAL: Force download by changing MIME type to octet-stream
      const forcedBlob = new Blob([blob], {
        type: "application/octet-stream",
      });

      // Method 1: Share API (best for mobile PWA)
      if (isPWA && navigator.share) {
        try {
          // ✅ FIXED: Use forcedBlob instead of original blob
          const file = new File([forcedBlob], fullFilename, {
            type: "application/octet-stream", // ← Prevents iOS from opening inline
          });

          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: "Majik Message Backup",
              text: "Your account backup key",
            });
            return;
          }
        } catch (err) {
          console.warn("Share failed:", err);
        }
      }

      // Method 2: File System Access API (desktop PWA only)
      if ("showSaveFilePicker" in window) {
        try {
          // ✅ Fixed: Added angle brackets < > around type parameters
          const fileTypes: Record<
            string,
            { description: string; accept: Record<string, string[]> }
          > = {
            json: {
              description: "JSON File",
              accept: { "application/json": [".json"] },
            },
            txt: {
              description: "Text File",
              accept: { "text/plain": [".txt"] },
            },
            pdf: {
              description: "PDF Document",
              accept: { "application/pdf": [".pdf"] },
            },
          };

          const fileConfig = fileTypes[fileType] || {
            description: `${fileType.toUpperCase()} File`,
            accept: {
              [blob.type || "application/octet-stream"]: [`.${fileType}`],
            },
          };

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: fullFilename,
            types: [fileConfig],
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err) {
          console.warn("File picker failed:", err);
        }
      }

      // Method 3: Traditional download (fallback)
      // ✅ Use forcedBlob to prevent inline display
      const url = URL.createObjectURL(forcedBlob);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fullFilename;
      anchor.rel = "noopener noreferrer";
      anchor.style.display = "none";

      document.body.appendChild(anchor);
      anchor.click();

      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(anchor);
      }, 100);
    },
  };
};

// Download Blob file
export function downloadBlob(
  blob: Blob,
  filetype: string,
  fileName: string,
): void {
  const url = URL.createObjectURL(blob);

  // Check if running as PWA (standalone mode)
  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any).standalone === true;

  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}.${filetype}`;

  if (isPWA) {
    // Force out of PWA into the device's default browser
    a.target = "_blank";
    a.rel = "noreferrer noopener";
  }

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Read Blob file and decode its content
export function readBlobFile(blob: Blob): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const content = event.target?.result;

      try {
        if (blob.type === "application/json") {
          const jsonContent = JSON.parse(content as string);
          resolve(jsonContent);
        } else if (blob.type === "text/plain" || blob.type === "") {
          const isBase64 = (str: string) => /^[A-Za-z0-9+/=]*$/.test(str);
          if (isBase64(content as string)) {
            const byteCharacters = atob(content as string);
            const byteNumbers = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            resolve(byteNumbers);
          } else {
            resolve(content);
          }
        } else if (blob.type.startsWith("application/octet-stream")) {
          const byteReader = new FileReader();
          byteReader.onload = (e) => {
            const byteArray = new Uint8Array(e.target?.result as ArrayBuffer);
            resolve(byteArray);
          };
          byteReader.readAsArrayBuffer(blob);
        } else {
          reject(new Error("Unsupported Blob type."));
        }
      } catch (error) {
        reject(new Error(`Error decoding the Blob file. ${error}`));
      }
    };

    reader.onerror = () => reject(new Error("Error reading the Blob file."));
    reader.readAsText(blob);
  });
}

// Enum for supported devices
export const ENUM_Devices = Object.freeze({
  ANDROID: "Mobile (Android)",
  IOS: "Mobile (Apple iOS)",
  WINDOWS: "Windows",
  MAC: "Macintosh",
  LINUX: "Linux",
  CHROME_OS: "ChromeOS",
  TABLET: "Tablet",
  OTHER: "Other",
});

// Detect device type
export function detectDevice(): string {
  const userAgent = navigator.userAgent;
  if (/android/i.test(userAgent)) {
    return ENUM_Devices.ANDROID;
  } else if (/iPad|iPhone|iPod/.test(userAgent)) {
    return ENUM_Devices.IOS;
  } else if (/Windows NT/.test(userAgent)) {
    return ENUM_Devices.WINDOWS;
  } else if (/Macintosh|Mac OS X/.test(userAgent)) {
    return ENUM_Devices.MAC;
  } else if (/CrOS/.test(userAgent)) {
    return ENUM_Devices.CHROME_OS;
  } else if (/Linux/.test(userAgent)) {
    return ENUM_Devices.LINUX;
  } else if (/Tablet|iPad/.test(userAgent)) {
    return ENUM_Devices.TABLET;
  } else {
    return ENUM_Devices.OTHER;
  }
}

// Check if the specified time has passed
export function hasTimePassed(time: number, originTime: string): boolean {
  const originDate = new Date(originTime);
  const currentDate = new Date();
  const elapsedTime = Math.floor(
    (currentDate.getTime() - originDate.getTime()) / 1000,
  );
  return elapsedTime >= time;
}

// Convert Base64 string to binary
export function base64ToBinary(base64String: string): {
  binaryData: Uint8Array;
  contentType: string;
} {
  let base64Data = base64String;
  let contentType = "";

  if (base64String.startsWith("data:")) {
    const matches = base64String.match(/^data:(.*?);base64,/);
    if (matches) {
      contentType = matches[1];
    }
    base64Data = base64String.split(",")[1];
  }

  const binaryData = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
  return { binaryData, contentType };
}

// Check if environment is development
export function isDevEnvironment(): boolean {
  const isNodeEnvOnDev = process.env.NODE_ENV === "development";

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const isHostOnDev = hostname === "localhost" || hostname === "127.0.0.1";
    return isNodeEnvOnDev || isHostOnDev;
  }

  return isNodeEnvOnDev;
}

/**
 * Compares two JSON objects and checks for any changes.
 *
 * @param {Object} json1 - The first JSON object to compare.
 * @param {Object} json2 - The second JSON object to compare.
 * @returns {boolean} - Returns true if there are changes, false if they are the same.
 */
export function checkForChangesJSON(json1: object, json2: object): boolean {
  const str1: string = JSON.stringify(json1);
  const str2: string = JSON.stringify(json2);
  return str1 !== str2;
}

/**
 * Checks if the current browser is Facebook or Messenger's in-app browser.
 *
 * @returns {boolean} - Returns `true` if the app is loaded in Facebook or Messenger's in-app browser (iOS or Android).
 */
export function isFacebookInAppBrowser(): boolean {
  const userAgent = navigator.userAgent;
  return /FBAN|FBAV|FBBV|FB_IAB|Messenger|MessengerForiOS/.test(userAgent);
}

/**
 * Checks if a Unix timestamp is still valid (greater than the current time).
 *
 * @param {number} unixTimestamp - The Unix timestamp to check.
 * @returns {boolean} Returns true if the timestamp is still valid (greater than current time), false otherwise.
 * @throws {Error} Throws an error if the unixTimestamp is not a valid Unix timestamp.
 */
export function isTimestampValid(unixTimestamp: number): boolean {
  if (typeof unixTimestamp !== "number" || isNaN(unixTimestamp)) {
    throw new Error("Invalid unixTimestamp: must be a number.");
  }
  const currentTime = Math.floor(Date.now() / 1000);
  return unixTimestamp > currentTime;
}

/**
 * Converts a Unix timestamp, Date object, ISO string, or null/undefined into a secure Base64 string.
 * Defaults to the current time if input is null or undefined.
 *
 * @param {(number | Date | string | null | undefined)} input - The time input.
 * @returns {string} A secure Base64 encoded string generated from the timestamp.
 * @throws {Error} Throws an error if the input is not a valid time format.
 */
export function secureTimecode(
  input?: number | Date | string | null | undefined,
): string {
  const unixTimestamp = (() => {
    if (input === null || input === undefined)
      return Math.floor(Date.now() / 1000);
    if (typeof input === "number") return input;
    if (input instanceof Date) return Math.floor(input.getTime() / 1000);
    if (typeof input === "string")
      return Math.floor(new Date(input).getTime() / 1000);
    throw new Error(
      "Invalid input type. Must be Unix timestamp, Date object, or ISO string.",
    );
  })();

  const unixArray = Array.from(String(unixTimestamp), Number);
  const reversedArray = [...unixArray].reverse();
  const invertedArray: number[] = [];

  for (let i = 0; i < unixArray.length; i++) {
    invertedArray.push(unixArray[i], reversedArray[i]);
  }

  return btoa(String.fromCharCode(...invertedArray));
}

/**
 * Decodes a Base64 string generated by secureTimecode back into a Unix timestamp.
 *
 * @param {string} base64String - The Base64 string to decode.
 * @returns {number} The decoded Unix timestamp.
 * @throws {Error} Throws an error if validation fails (reversed array doesn't match original).
 */
export function decodeSecureTimecode(base64String: string): number {
  const decodedArray = Array.from(atob(base64String)).map((char) =>
    char.charCodeAt(0),
  );

  const originalArray = decodedArray.filter((_, i) => i % 2 === 0);
  const reversedArray = decodedArray.filter((_, i) => i % 2 !== 0).reverse();

  if (originalArray.join("") !== reversedArray.join("")) {
    throw new Error(
      "Validation failed: reversed array does not match the original array.",
    );
  }

  return parseInt(originalArray.join(""), 10);
}

/**
 * Checks if a Unix timestamp has expired based on a specified duration in seconds.
 *
 * @param {number} durationInSeconds - The allowed duration in seconds for the timestamp.
 * @param {number} unixTimestamp - The Unix timestamp to check.
 * @returns {boolean} Returns true if the timestamp has expired, false otherwise.
 * @throws {Error} Throws an error if the unixTimestamp is not a valid Unix timestamp.
 */
export function isTimestampExpired(
  durationInSeconds: number,
  unixTimestamp: number,
): boolean {
  if (typeof unixTimestamp !== "number" || isNaN(unixTimestamp)) {
    throw new Error("Invalid unixTimestamp: must be a number.");
  }
  const currentTime = Math.floor(Date.now() / 1000);
  return currentTime > unixTimestamp + durationInSeconds;
}

/**
 * Checks if a decoded Base64 timecode has expired based on a specified duration in seconds.
 *
 * @param {string} base64String - The Base64 encoded timecode.
 * @param {number} durationInSeconds - The allowed duration in seconds.
 * @returns {boolean} Returns true if the decoded Unix timestamp has expired, false otherwise.
 * @throws {Error} Throws an error if validation of the decoded timecode fails.
 */
export function isSTXExpired(
  base64String: string,
  durationInSeconds: number,
): boolean {
  const unixTimestamp = decodeSecureTimecode(base64String);
  return isTimestampExpired(durationInSeconds, unixTimestamp);
}

/**
 * Determines whether the user is accessing the application from a mobile device.
 *
 * @returns {boolean} - Returns true if the device is likely mobile, otherwise false.
 */
export function isOnMobile(): boolean {
  const userAgent = navigator.userAgent;
  const mobileUserAgents = [
    "Android",
    "iPhone",
    "iPad",
    "iPod",
    "Windows Phone",
    "Opera Mini",
    "IEMobile",
    "Mobile",
    "BlackBerry",
    "webOS",
    "Silk",
  ];

  const hasTouchScreen = (): boolean =>
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isScreenSmall = window.innerWidth <= 800 && window.innerHeight <= 800;
  const isMobileUserAgent = mobileUserAgents.some((agent) =>
    userAgent.includes(agent),
  );

  return isMobileUserAgent || hasTouchScreen() || isScreenSmall;
}

/**
 * Capitalizes the first letter of each word in a given string.
 *
 * @param {string} str - The string to capitalize.
 * @returns {string} The capitalized string.
 */
export function capitalizeWords(str: string): string {
  if (typeof str !== "string") throw new Error("Input must be a string");
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Generates a SHA-256 hash for a given string.
 * Validates that the input is a non-empty string.
 * @param {string} string - The string to hash.
 * @returns {string} The hash of the string in hexadecimal format.
 * @throws {Error} If the input is not a valid non-empty string.
 */
export function hashString(string: string): string {
  const hash = cryptoJS.SHA256(string);
  return hash.toString(cryptoJS.enc.Hex);
}

/**
 * Calculate age based on a birthday, either as an ISO 8601 string or a Date object.
 * @param birthday - The birthday as an ISO 8601 string (e.g., "1990-12-07") or a Date object.
 * @returns The age as a number.
 */
export function computeAgeFromBirthday(birthday: string | Date): number {
  // Convert to Date if input is a string
  const birthDate =
    typeof birthday === "string" ? new Date(birthday) : birthday;

  if (isNaN(birthDate.getTime())) {
    throw new Error(
      "Invalid date input. Please provide a valid ISO string or Date object.",
    );
  }

  const today = new Date();
  const yearsDifference = today.getFullYear() - birthDate.getFullYear();

  const isBirthdayPassedThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  return isBirthdayPassedThisYear ? yearsDifference : yearsDifference - 1;
}

/**
 * Splits the input string using the specified delimiter and returns the part at the given index.
 *
 * @param input - The input string to split.
 * @param index - The zero-based index of the desired part.
 * @param delimiter - The string used to split the input string.
 * @param defaultValue - (Optional) A fallback value to return if the index is out of bounds.
 *
 * @returns The string at the given index, or the default value if provided, otherwise null.
 */
export function getStringPart(
  input: string,
  index: number,
  delimiter: string,
): string | null {
  // Validate input parameters
  if (typeof input !== "string")
    throw new Error("Parameter 'input' must be a string.");
  if (typeof index !== "number")
    throw new Error("Parameter 'index' must be a number.");
  if (typeof delimiter !== "string")
    throw new Error("Parameter 'delimiter' must be a string.");

  // Split the input string based on the delimiter
  const parts = input.split(delimiter);

  // Return the value at the given index if it exists; otherwise, return the defaultValue
  if (index >= 0 && index < parts.length) {
    return parts[index];
  } else {
    return null;
  }
}

/**
 * Checks if the part of the input string at the specified index matches the given value.
 *
 * @param input - The input string to split.
 * @param index - The zero-based index of the desired part.
 * @param delimiter - The string used to split the input string.
 * @param value - The value to compare the extracted part with.
 *
 * @returns True if the extracted part equals the value; otherwise, false.
 */
export function isStringPartEqualTo(
  input: string,
  index: number,
  delimiter: string,
  value: string,
): boolean {
  try {
    // Reuse getStringPart to extract the part at the specified index
    const extractedPart = getStringPart(input, index, delimiter);

    // Compare the extracted part to the value and return the result
    return extractedPart === value;
  } catch (error) {
    console.log(error);
    return false;
  }
}

/**
 * Transforms the input text based on the specified capitalization mode.
 *
 * @param text - The input string to be transformed.
 * @param mode - The capitalization mode to apply. Can be one of the following:
 *   - "word": Capitalizes the first letter of every word separated by whitespace.
 *   - "character": Converts the entire string to uppercase.
 *   - "sentence": Capitalizes the first letter of every sentence, defined as text following a period and a space.
 *   - "first": (Default) Capitalizes only the first character of the entire string.
 * @returns The transformed string based on the selected mode.
 */
export function autocapitalize(
  text: string,
  mode: "word" | "character" | "sentence" | "first" = "first",
): string {
  if (!text) return "";

  switch (mode) {
    case "word":
      return text
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    case "character":
      return text.toUpperCase();

    case "sentence":
      return text.replace(/(^|\.\s+)([^\s\n])/g, (match, prefix, char) => {
        // Prefix captures the period and following space, including newlines
        // Char is the first character after the prefix
        return prefix + char.toUpperCase();
      });

    case "first":
    default:
      return text.charAt(0).toUpperCase() + text.slice(1);
  }
}

/**
 * Checks if the specified time has passed relative to the current time.
 *
 * @param {string} dateTime - The target date and time in ISO string format (e.g., "2025-01-01T17:30:00Z").
 * @param {number} time - The amount of time (in the specified unit) to check if has passed since the given `dateTime`.
 * @param {'SECONDS' | 'MINUTES' | 'HOURS' | 'DAYS' | 'MONTHS' | 'QUARTER' | 'YEAR'} type - The unit of time for comparison. Default is 'MINUTES'.
 *
 * @returns {boolean} - Returns `true` if the specified time has passed based on the given `type` and `time`; otherwise, returns `false`.
 *
 * @example
 * hasTimeElapsed("2025-01-01T17:30:00Z", 6, 'HOURS'); // Returns `true` if the current time is at least 6 hours later than the given dateTime
 */
export function hasTimeElapsed(
  dateTime: string,
  time: number,
  type:
    | "SECONDS"
    | "MINUTES"
    | "HOURS"
    | "DAYS"
    | "MONTHS"
    | "QUARTER"
    | "YEAR" = "MINUTES",
): boolean {
  const now = new Date();
  const targetDate = new Date(dateTime);

  // Function to get the time difference in the specified unit
  const getTimeDifference = (
    target: Date,
    current: Date,
    unit: string,
  ): number => {
    const diffInMs = target.getTime() - current.getTime();

    switch (unit) {
      case "SECONDS":
        return diffInMs / 1000;
      case "MINUTES":
        return diffInMs / (1000 * 60);
      case "HOURS":
        return diffInMs / (1000 * 60 * 60);
      case "DAYS":
        return diffInMs / (1000 * 60 * 60 * 24);
      case "MONTHS":
        return diffInMs / (1000 * 60 * 60 * 24 * 30); // Approximation (30 days per month)
      case "QUARTER":
        return diffInMs / (1000 * 60 * 60 * 24 * 90); // Approximation (90 days per quarter)
      case "YEAR":
        return diffInMs / (1000 * 60 * 60 * 24 * 365); // Approximation (365 days per year)
      default:
        return 0;
    }
  };

  const difference = getTimeDifference(targetDate, now, type);

  // Return true if the target time has passed by the specified amount
  return difference >= time;
}

/**
 * Extracts the YouTube video ID from a given URL string.
 *
 * @param url - The full YouTube URL (e.g., https://www.youtube.com/watch?v=LUkf3CK1NR0).
 * @returns The extracted 11-character video ID if found, or null if the input is invalid or not a recognized YouTube URL.
 */
export function extractYouTubeVideoID(url: string): string | null {
  const regex =
    /(?:youtube\.com\/(?:shorts\/|(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=))|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

/**
 * Extracts the Spotify song ID from a given Spotify track URL.
 *
 * @param url - The full Spotify track URL (e.g., embed or regular track link).
 * @returns The Spotify song ID as a string, or null if not found.
 */
export function extractSpotifySongID(url: string): string | null {
  const match = url.match(/\/track\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

export async function convertToBase64FromUrl(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();
  console.log("Converted Blob: ", blob);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export enum URLType {
  Website = "Website",
  Image = "Image",
  Youtube = "Youtube",
  Facebook = "Facebook",
  X = "X",
  Threads = "Threads",
  Instagram = "Instagram",
  Spotify = "Spotify",
  Soundcloud = "Soundcloud",
  Canva = "Canva",
  Pinterest = "Pinterest",
}

export function identifyURLType(url: string): URLType {
  const lowerUrl = url.toLowerCase();

  // Check for image extensions
  const imageExtensions = [
    ".webp",
    ".png",
    ".jpeg",
    ".jpg",
    ".tiff",
    ".bmp",
    ".gif",
    ".svg",
  ];
  if (imageExtensions.some((ext) => lowerUrl.includes(ext))) {
    return URLType.Image;
  }

  // Youtube
  if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
    return URLType.Youtube;
  }

  // Facebook / Instagram
  if (lowerUrl.includes("facebook.com") || lowerUrl.includes("fb.com")) {
    return URLType.Facebook;
  }

  //  Instagram
  if (lowerUrl.includes("instagram.com")) {
    return URLType.Instagram;
  }

  //  Threads
  if (lowerUrl.includes("threads.net")) {
    return URLType.Threads;
  }

  //  X (formerly Twitter)
  if (
    lowerUrl.includes("twitter.com") || // aka "X"
    lowerUrl.includes("x.com")
  ) {
    return URLType.X;
  }

  // Spotify
  if (lowerUrl.includes("spotify.com")) {
    return URLType.Spotify;
  }

  // Soundcloud
  if (lowerUrl.includes("soundcloud.com")) {
    return URLType.Soundcloud;
  }

  // Canva
  if (lowerUrl.includes("canva.com")) {
    return URLType.Canva;
  }

  // Pinterest
  if (lowerUrl.includes("pinterest.com")) {
    return URLType.Pinterest;
  }

  // Default fallback
  return URLType.Website;
}

export function displayFormattedTIN(tin: number | string): string {
  const tinStr = tin.toString().padStart(12, "0"); // ensure 12 digits
  return tinStr.replace(/^(\d{3})(\d{3})(\d{3})(\d{3})$/, "$1-$2-$3-$4");
}

export interface PinterestAuthCode {
  code: string;
  state: string;
}

export function getPinterestParams(): PinterestAuthCode | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");
  const state = urlParams.get("state");

  if (code && state) {
    return { code, state };
  }

  return null;
}


export function extractEmailDomain(email: string): string | null {
  const atIndex = email.lastIndexOf("@");
  if (atIndex === -1) return null;
  return email.slice(atIndex + 1).toLowerCase();
}

export type PasswordRequirement =
  | "NONE"
  | "DEFAULT"
  | "LETTERS-DIGITS"
  | "CASED-DIGITS"
  | "CASED-DIGITS-SYMBOLS";

/**
 * Validates a password string against specified strength requirements.
 *
 * @param {string} password - The password string to validate.
 * @param {number} [minLength=8] - The minimum required length of the password. Defaults to 8.
 * @param {"DEFAULT" | "LETTERS-DIGITS" | "CASED-DIGITS" | "CASED-DIGITS-SYMBOLS"} [requirements="DEFAULT"]
 * - The password strength requirements:
 *   - "DEFAULT": Only checks the minimum length.
 *   - "LETTERS-DIGITS": Requires at least one digit.
 *   - "CASED-DIGITS": Requires both uppercase and lowercase letters, and at least one digit.
 *   - "CASED-DIGITS-SYMBOLS": Requires uppercase, lowercase, at least one digit, and at least one symbol.
 *
 * @returns {boolean} Returns `true` if the password meets the specified requirements, otherwise `false`.
 *
 * @example
 * isPasswordValidSafe("Password1"); // true (with default settings)
 * isPasswordValidSafe("pass1234", 8, "CASED-DIGITS"); // false (missing uppercase)
 * isPasswordValidSafe("Pass1234!", 8, "CASED-DIGITS-SYMBOLS"); // true
 */

export function isPasswordValidSafe(
  password: string,
  minLength: number = 8,
  requirements: PasswordRequirement = "DEFAULT",
): boolean {
  if (!password?.trim()) return false;
  if (requirements === "NONE") return true;
  if (password.length < minLength) return false;

  switch (requirements) {
    case "DEFAULT":
      return true;

    case "LETTERS-DIGITS":
      return /\d/.test(password);

    case "CASED-DIGITS":
      return (
        /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password)
      );

    case "CASED-DIGITS-SYMBOLS":
      return (
        /[a-z]/.test(password) &&
        /[A-Z]/.test(password) &&
        /\d/.test(password) &&
        /[^a-zA-Z0-9]/.test(password)
      );

    default:
      return true;
  }
}
/**
 * Converts an image URL into a File object, with automatic MIME type detection
 * and filename extension correction based on the image's actual format.
 *
 * @param {string} url - Publicly accessible image URL.
 * @param {string} fileName - Desired base name of the file (extension will be corrected if needed).
 * @param {string} [mimeType] - Optional MIME type override (e.g. "image/jpeg").
 *
 * @returns {Promise<File>} - A File object with accurate MIME type and extension.
 *
 * @throws {Error} - If the image cannot be fetched or parsed.
 *
 * @example
 * const file = await imageURLtoBlob('https://example.com/pic.png', 'my-cover');
 * uploadToSoundCloud(file);
 */
export async function imageURLtoBlob(
  url: string,
  fileName: string,
  mimeType?: string,
): Promise<File> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image from URL: ${url}`);
  }

  const blob = await response.blob();
  const detectedMime = mimeType || blob.type;

  // Fallback to 'image/jpeg' if MIME couldn't be determined
  const finalMime = detectedMime || "image/jpeg";

  // Determine correct extension
  let extension = "";
  switch (finalMime) {
    case "image/jpeg":
      extension = ".jpg";
      break;
    case "image/png":
      extension = ".png";
      break;
    case "image/webp":
      extension = ".webp";
      break;
    case "image/gif":
      extension = ".gif";
      break;
    default:
      extension = ""; // fallback or unknown
  }

  // Ensure the filename ends with the correct extension
  const sanitizedName = fileName.replace(/\.[^/.]+$/, ""); // remove current extension if any
  const finalFileName = `${sanitizedName}${extension}`;

  return new File([blob], finalFileName, { type: finalMime });
}

export type FileSizeUnit = "KB" | "MB" | "GB" | "TB" | "PB" | "AUTO";

/**
 * Converts bytes into a human-readable string in the specified unit or automatically.
 *
 * @param bytes - The number of bytes to convert
 * @param output - The desired output unit or "AUTO" to pick the most appropriate. Defaults to `AUTO`.
 * @returns A formatted string like "3.42 MB" or "1.2 GB"
 */
export function convertFileBytes(
  bytes: number,
  output: FileSizeUnit = "AUTO",
): string {
  const units = ["KB", "MB", "GB", "TB", "PB"] as const;

  const unitMap: Record<Exclude<FileSizeUnit, "AUTO">, number> = {
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
    TB: 1024 ** 4,
    PB: 1024 ** 5,
  };

  if (output === "AUTO") {
    for (let i = units.length - 1; i >= 0; i--) {
      const unit = units[i];
      const value = bytes / unitMap[unit];
      if (value >= 1 && value < 10_000) {
        return `${value.toFixed(2)} ${unit}`;
      }
    }
    return `${(bytes / 1024).toFixed(2)} KB`; // Fallback
  }

  const divisor = unitMap[output];
  if (!divisor) {
    throw new Error(`Invalid output unit: ${output}`);
  }

  const value = bytes / divisor;
  return `${value.toFixed(2)} ${output}`;
}

/**
 * Parses a Date object or ISO string into a Google Calendar–compatible object
 * with all three properties: date, dateTime, and timeZone.
 *
 * @param input - A Date object or an ISO 8601 string
 * @param timeZone - Optional. Defaults to "Asia/Manila"
 * @returns An object with .date, .dateTime, and .timeZone for Google Calendar
 */
export function parseDateForGoogleCalendar(
  input: Date | string,
  timeZone: string = "Asia/Manila",
): {
  date: string;
  dateTime: string;
  timeZone: string;
} {
  const dateObj = typeof input === "string" ? new Date(input) : input;

  if (isNaN(dateObj.getTime())) {
    throw new Error("Invalid date input.");
  }

  const isoString = dateObj.toISOString(); // RFC3339 UTC
  const date = isoString.split("T")[0]; // "yyyy-mm-dd"

  return {
    date,
    dateTime: isoString,
    timeZone,
  };
}

const normalizePath = (path: string) => path.replace(/\/+$/, "");

const waitForPathChange = (
  expectedPath: string,
  timeout = 20000,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const interval = setInterval(() => {
      if (
        normalizePath(window.location.pathname) === normalizePath(expectedPath)
      ) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - start > timeout) {
        clearInterval(interval);
        window.location.href = expectedPath;
        reject(new Error("Navigation timeout exceeded"));
      }
    }, 50);
  });
};

let isNavigating = false;

/**
 * Preloads a navigation with a toast loader and success/error feedback
 * @param path - The target path to navigate
 * @param router - Next.js router instance if not using global import
 * @param options - Optional toast message overrides
 */
export async function preloadedNavigation(
  path: string,
  router: ReturnType<typeof useRouter>,
  options?: {
    loadingMessage?: string;
    successMessage?: string;
    errorMessage?: string;
  },
): Promise<void> {
  if (isNavigating) {
    console.warn("Navigation already in progress, ignoring duplicate trigger.");
    return;
  }

  isNavigating = true;
  const routerInstance = router;

  const navigateWithPreloader = async () => {
    try {
      // Attempt client-side navigation first
      routerInstance.push(path);

      // Wait until the path actually changes
      await waitForPathChange(path);
    } catch (error) {
      console.warn("Navigation issue detected, forcing hard reload:", error);

      // Fallback: force a full page reload to the correct URL
      window.location.href = path;
    }
  };

  toast.promise(navigateWithPreloader(), {
    loading: options?.loadingMessage ?? "Loading page...",
    success: () => {
      isNavigating = false;

      return options?.successMessage ?? "Navigation successful!";
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: (error: any) =>
      options?.errorMessage ?? `Navigation failed: ${error.message}`,
  });
}

export async function parseImageForPDF(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Deep clone an object and remove any functions, making it fully JSON-safe.
 */
export function stripFunctions<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof obj === "function") return undefined as any;

  if (Array.isArray(obj)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return obj.map(stripFunctions) as any;
  }

  if (typeof obj === "object") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const val = obj[key];
        if (typeof val !== "function") {
          result[key] = stripFunctions(val);
        }
      }
    }
    return result;
  }

  // primitive value (string, number, boolean, null, undefined)
  return obj;
}

export function base64Encode(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

export function base64Decode(base64: string): string {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
